import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { ThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { loginTheme } from "../theme";
import MicrosoftIcon from "../assets/microsoft-logo.svg";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box px={0} py={2}>
          {children}
        </Box>
      )}
    </Typography>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

export default () => {
  const [searchParams, setSearchParams] = useState(window.location.search);
  const [value, setValue] = React.useState(0);
  const [loginMethods, setLoginMethods] = React.useState([""]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Effect to fetch login methods
  useEffect(() => {
    fetchLogin();
  }, []);

  // Effect to handle Microsoft login
  useEffect(() => {
    const queryParams = new URLSearchParams(searchParams);
    if (queryParams.has("code")) {
      handleMSFT();
    }
    // This effect depends on `searchParams`, so it runs when `searchParams` changes.
  }, [searchParams]);

  // Function to listen to URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      setSearchParams(window.location.search);
    };

    // Listen for URL changes
    window.addEventListener("popstate", handleUrlChange);
    window.addEventListener("pushState", handleUrlChange);
    window.addEventListener("replaceState", handleUrlChange);

    // Clean up
    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      window.removeEventListener("pushState", handleUrlChange);
      window.removeEventListener("replaceState", handleUrlChange);
    };
  }, []);

  useEffect(() => {
    //handle errors in searchParams
    const queryParams = new URLSearchParams(searchParams);
    if (queryParams.has("error")) {
      const error = queryParams.get("error");
      console.error(`Error logging in: ${error}`);
      alert(`Error logging in: ${error}`);
      //wipe the query string from the url
      window.history.replaceState({}, document.title, "/login");
    }
  }, [searchParams]);

  async function fetchLogin() {
    console.log("fetching login methods");

    const response = await fetch("/api/auth_modes");
    const options = await response.json();
    //microsoft and email are the only options for now, but we can add more later
    setLoginMethods(options);
  }

  async function handleMSFT() {
    //if the url contains "code" then we know the user has been redirected from Microsoft
    const msftEndpoint = "/api/auth/microsoft/callback";
    try {
      const callbackString = window.location.href.split("?")[1];
      const url = `${msftEndpoint}?${callbackString}`;
      const response = await fetch(url);
      //if response is ok, then the user has been authenticated and we can redirect them to the home page
      if (response.status === 200) {
        window.location.replace("/");
      } else {
        throw new Error("Microsoft login failed");
      }
    } catch (error) {
      //this is fine, we just don't want to redirect the user
      console.warn("There was an error logging in with Microsoft");
      //wipe the query string from the url
      window.history.replaceState({}, document.title, "/login");
    }
  }
  const renderLoginMethod = (method) => {
    switch (method) {
      case "microsoft":
        return (
          <form action="/api/login/microsoft" method="post">
            <Box p={2} display="flex" justifyContent="center">
              {/* Placeholder for Microsoft sign-in logic */}
              <Button variant="outlined" color="primary" type="submit">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={MicrosoftIcon}
                    style={{ height: "20px", paddingRight: "10px" }}
                    alt="sign in with msft"
                  />
                  <span
                    style={{
                      height: "100%",
                      textAlign: "center",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    Sign in with Microsoft
                  </span>
                </div>
              </Button>
            </Box>
          </form>
        );
      case "email":
        return (
          <form action="/api/login/email" method="post">
            <TextField label="Email" name="email" fullWidth required />
            <TextField
              label="Password"
              type="password"
              name="password"
              fullWidth
              required
            />
            <Box pt={2} display="flex" justifyContent="center">
              <Button variant="contained" color="primary" type="submit">
                Login
              </Button>
            </Box>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={createMuiTheme(loginTheme)}>
      <React.Fragment>
        <CssBaseline />
        <Container>
          <Grid container spacing={3} justify="center">
            <Grid item xs={12} sm={8} md={6}>
              <Card>
                <CardContent>
                  <AppBar position="static">
                    <Tabs
                      value={value}
                      onChange={handleChange}
                      aria-label="sign in methods"
                      centered
                    >
                      {loginMethods.map((method, index) => (
                        <Tab
                          label={
                            method.charAt(0).toUpperCase() + method.slice(1)
                          }
                          {...a11yProps(index)}
                          key={index}
                        />
                      ))}
                    </Tabs>
                  </AppBar>
                  {loginMethods.map((method, index) => (
                    <TabPanel value={value} index={index} key={index}>
                      {renderLoginMethod(method)}
                    </TabPanel>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </React.Fragment>
    </ThemeProvider>
  );
};
